import React from 'react';
import _ from 'lodash';
import { AnalyticsReport } from 'store/analytics/ui/analyticsUiTypes';
import BarsChart from 'views/Analytics/AnalyticsMockService/Reporting/Reports/BarsChart';
import { DateRange } from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import { MockServiceAnalytics, MsType } from 'types/rest/mockServiceTypes';
import NoReportDataPlaceholder from 'views/Analytics/AnalyticsMockService/Reporting/NoReportDataPlaceholder/NoReportDataPlaceholder';
import { ReportsData } from 'store/analytics/data/analyticsDataTypes';
import { transformRawData } from 'utils/analyticsUtils';
import FullPageLoader from 'components/loaders/FullPageLoader';
import LinesChart from 'views/Analytics/AnalyticsMockService/Reporting/Reports/LinesChart';
import {
  MOCK_SERVICE_FORCED_DELAY,
  MOCK_SERVICE_RESPONSE_TIME,
  MOCK_SERVICE_TPS,
  TRANSACTION_DAILY_COUNTS,
  TRANSACTION_HITS_AND_MISSES,
} from 'store/analytics/analyticsReportTypes';
import { bzmUTCformat, formatDateAndTime, isSameDate } from 'utils/dateUtil';
import { AnalyticsResponse, MetricResolution } from 'types/rest/analyticsTypes';
import { TooltipOptions } from 'highcharts';
import { defaultHeaderFormatDate, defaultHeaderFormatDateTime } from 'constants/chart-constants';

/**
 * Props which go to the chart.
 */
export type ChartProps = {
  /** Represents highchart series. */
  chartSeries: ChartSeries[];

  /** x-axis categories. The number has to match chart series. */
  categories?: string[];

  /** Suffix for the values in the y axis */
  yAxisUnitSuffix?: string;

  /** Miniumum for the x axis*/
  min?: number;

  /** Maximum for the x axis */
  max?: number;

  tickInterval?: number;

  useUTC?: boolean;

  tooltip?: TooltipOptions;
};

/**
 * Report Wrapper Props
 */
export type ReportProps = {
  isLoadingReportData: boolean;

  report: AnalyticsReport;

  /** Selected Date Range for report */
  dateRange: DateRange;

  /** List of selected Virtual Services */
  selectedMockServices: MockServiceAnalytics[];

  /** Data for selected report */
  reportData: ReportsData | null;

  /** Time resolution being used */
  resolution: MetricResolution;

  useUTC: boolean;

  msType: MsType;
};

export interface ChartSeries {
  name: string;
  color: string;
  borderColor: string;
  data: any[];
  toolTip?: any;
}

type ChartExtremes = {
  min?: number;
  max?: number;
};

/**
 * Report wrapper. It will do the decision which chart to display based on current ui state values.
 * @constructor
 */
const Report = ({ report, reportData, isLoadingReportData, dateRange, resolution, useUTC, msType }: ReportProps) => {
  const placeholder = <NoReportDataPlaceholder reportName={report.name} key={report.id} />;

  /**
   * Prepares tick interval based on report type and data coming from server. If there are more points (data)
   * on x axis, higcharts correctly determines the tick count based on the gap between the data. If there is just
   * single point, we need to define gap manually.
   */
  const getTickInterval = (rawData: any, resolution: MetricResolution) => {
    if (isSingleTimestampPresent(rawData)) {
      // Create tick interval based on the chosen resolution
      const dayInterval = 24 * 3600 * 1000;
      switch (resolution) {
        case '1D':
          return dayInterval;
        case '1W':
          return dayInterval * 7;
        case '1M':
          return dayInterval * 30;
      }
    }
    return undefined;
  };

  /**
   * Checks whether there is only single timestamp value in the metrics.
   * @param rawData
   */
  const isSingleTimestampPresent = (rawData: any) => {
    if (_.isNil(rawData)) {
      return false;
    }

    const timestamps: any[] = _.chain(rawData.metrics)
      .map('timestamp')
      .uniq()
      .value();

    return timestamps.length === 1;
  };

  /**
   * Defines minimum & maximum for the chart X-Axis.
   * @param from
   * @param to
   */
  const getRangeExtremes = (rawData: AnalyticsResponse, from?: Date | null, to?: Date | null) => {
    const extremes: ChartExtremes = {
      min: undefined,
      max: undefined,
    };

    if (_.isNil(from) || _.isNil(to)) {
      return extremes;
    }

    // Dates are not within the same calendar day, do not set extremes and let the highcharts
    // figure boundaries. If it range is different, try to use start & end time stamps from the server.
    if (!isSameDate(from, to)) {
      extremes.max = rawData.to ? new Date(rawData.to).getTime() : to.getTime();
      extremes.min = rawData.from ? new Date(rawData.from).getTime() : from.getTime();
    }

    return extremes;
  };

  const getChartHeaderDom = (rawData: AnalyticsResponse) => {
    let reportDateFrom;
    let reportDateto;
    if (useUTC) {
      reportDateFrom = rawData.from ? bzmUTCformat(new Date(rawData.from)) : 'N/A';
      reportDateto = rawData.to ? bzmUTCformat(new Date(rawData.to)) + ' UTC' : 'N/A';
    } else {
      reportDateFrom = rawData.from ? formatDateAndTime(new Date(rawData.from)) : 'N/A';
      reportDateto = rawData.to ? formatDateAndTime(new Date(rawData.to)) : 'N/A';
    }

    return (
      <div className="text-center">
        <span>
          <span className="text-muted">Report Time Range: </span>
          {reportDateFrom} - {reportDateto}
        </span>
      </div>
    );
  };

  /**
   * Checks if raw data object from backend contains some metric data.
   * @param rawData
   */
  const containsData = (rawData: any) => {
    return !(
      _.isNil(rawData) ||
      _.isEmpty(rawData) ||
      (isLoadingReportData === false && rawData.metrics && rawData.metrics.length === 0)
    );
  };

  /**
   * Based on a type of the report display different data.
   */
  const createReportDom = () => {
    if (_.isNil(reportData)) {
      return null;
    }

    // Show placeholder if metric data is empty
    const { rawData } = reportData;
    let chartContent = placeholder;
    let chartHeader = null;

    if (containsData(rawData)) {
      //Prepare header
      chartHeader = getChartHeaderDom(rawData);

      //Prepare content
      switch (report.id) {
        case TRANSACTION_HITS_AND_MISSES:
        case TRANSACTION_DAILY_COUNTS:
          const tickInterval = getTickInterval(rawData, resolution);
          const rangeExtremes = getRangeExtremes(rawData, dateRange.from, dateRange.to);
          let barchartTooltip: TooltipOptions = {
            headerFormat: defaultHeaderFormatDate,
            shared: false,
            useHTML: true,
          };

          // Override default point format for TRANSACTION_HITS_AND_MISSES
          if (report.id === 'TRANSACTION_HITS_AND_MISSES') {
            barchartTooltip = {
              ...barchartTooltip,
              pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}',
            };
          }

          chartContent = (
            <BarsChart
              tooltip={barchartTooltip}
              min={rangeExtremes.min}
              max={rangeExtremes.max}
              chartSeries={transformRawData(rawData, msType)}
              tickInterval={tickInterval}
              useUTC={useUTC}
            />
          );
          break;
        case MOCK_SERVICE_RESPONSE_TIME:
        case MOCK_SERVICE_TPS:
        case MOCK_SERVICE_FORCED_DELAY:
          const lineChartTooltip: TooltipOptions = {
            headerFormat: defaultHeaderFormatDateTime,
            shared: true,
            useHTML: true,
          };
          chartContent = (
            <LinesChart
              tooltip={lineChartTooltip}
              yAxisUnitSuffix={report.id === MOCK_SERVICE_TPS ? '' : 'ms'}
              chartSeries={transformRawData(rawData, msType)}
              useUTC={useUTC}
            />
          );
          break;
        default:
          return placeholder;
      }
    }

    return (
      <div className="position-relative">
        {isLoadingReportData ? (
          <div style={{ height: 500 }}>
            <FullPageLoader loadingText={'Loading Chart Data...'} />
          </div>
        ) : (
          <div>
            {chartHeader}
            {chartContent}
          </div>
        )}
      </div>
    );
  };

  return <div className={'analytics-report'}>{createReportDom()}</div>;
};

export default Report;
